import React from "react";
import {BarcodeScanner as DynamsoftScanner} from "dynamsoft-barcode-reader-bundle";
import * as Dynamsoft from "dynamsoft-barcode-reader-bundle";
import request from "../../../util/SuperagentUtils";
import "./style/BarcodeScanner.css";

/*
 * KNOWN ISSUE: this component was written against the legacy Dynamsoft v9 API
 * (`dynamsoft-javascript-barcode`), but now imports `dynamsoft-barcode-reader-bundle` v11.
 * In v11, `BarcodeScanner` has no static `createInstance()` (it is constructed with a config and
 * started via `launch()`), so `createInstance()` below throws a TypeError at runtime. The error is
 * caught and logged, and the scanner never opens.
 *
 * The interfaces below describe the legacy v9 scanner surface this code relies on, so the existing
 * behavior can be preserved and typed during the TS migration. Fixing the scanner requires a rewrite
 * against the v11 API (`new BarcodeScanner(config).launch()`), which is out of scope for the migration.
 */
interface LegacyRuntimeSettings {
  barcodeFormatIds: number | bigint;
}

interface LegacyTextResult {
  barcodeText: string;
}

interface LegacyBarcodeScanner {
  destroy(): void;
  setUIElement(element: HTMLElement | null): Promise<void>;
  getRuntimeSettings(): Promise<LegacyRuntimeSettings>;
  updateRuntimeSettings(settings: LegacyRuntimeSettings): Promise<void>;
  onFrameRead?: (results: LegacyTextResult[]) => void;
  onUniqueRead?: (text: string) => void;
  pauseScan(): void;
  open(): Promise<void>;
  setResolution(width: number, height: number): Promise<void>;
}

interface LegacyBarcodeScannerStatic {
  createInstance(): Promise<LegacyBarcodeScanner>;
}

const LegacyDynamsoftScanner = DynamsoftScanner as unknown as LegacyBarcodeScannerStatic;

interface OcrKeysResponse {
  dynamsoftKey?: string;
}

interface BarcodeScannerProps {
  /** Called with the decoded barcode text when a barcode is read */
  readScannedResults: (barcodeText: string) => void;
}

// This class should contain no internal React state and instead should rely on props
const BarcodeScanner = ({readScannedResults}: BarcodeScannerProps) => {
  const elRef = React.useRef<HTMLDivElement>(null);
  const scannerRef = React.useRef<LegacyBarcodeScanner | null>(null);
  const destroyedRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    const initScanner = async (): Promise<void> => {
      try {
        // Only load product keys and resource path on first time initialization
        if (!Dynamsoft.CoreModule.isModuleLoaded('license')) {
          const response = await request
            .get("/api/ocr-keys")
            .then((json) => {
              const result: OcrKeysResponse | null = JSON.parse(json.text);
              return result?.dynamsoftKey;
            });

          if (!response) {
            throw new Error("Failed to fetch Dynamsoft license key.");
          }
          // The v9 call passed ["dbr"]; v11's loadWasm() takes no arguments and ignored it.
          await Dynamsoft.CoreModule.loadWasm();
          await Dynamsoft.LicenseManager.initLicense(response);
        }

        const scanner = scannerRef.current || await LegacyDynamsoftScanner.createInstance();
        scannerRef.current = scanner;

        if (destroyedRef.current) {
          scanner.destroy();
          return;
        }

        await scanner.setUIElement(elRef.current);

        //Set scanner to read PDF417
        const runtimeSettings = await scanner.getRuntimeSettings();
        runtimeSettings.barcodeFormatIds = Dynamsoft.EnumBarcodeFormat.BF_PDF417;
        await scanner.updateRuntimeSettings(runtimeSettings);

        scanner.onFrameRead = results => {
          if (results.length) {
            scanner.pauseScan();
            readScannedResults(results[0].barcodeText);
          }
        };

        scanner.onUniqueRead = (txt) => {
          scanner.pauseScan();
          readScannedResults(txt);
        };

        await scanner.open();
        await scanner.setResolution(1280, 720);
      } catch (ex) {
        console.log((ex as {message?: string}).message);
        console.error(ex);
      }
    };

    void initScanner();

    return () => {
      destroyedRef.current = true;
      if (scannerRef.current) {
        scannerRef.current.destroy();
      }
    };
  }, [readScannedResults]);

  return (
    <div ref={elRef} className="component-barcode-scanner">
      <svg className="dbrScanner-bg-loading" viewBox="0 0 1792 1792"/>
      <svg className="dbrScanner-bg-camera" viewBox="0 0 2048 1792">
        <path
          d="M1024 672q119 0 203.5 84.5t84.5 203.5-84.5 203.5-203.5 84.5-203.5-84.5-84.5-203.5 84.5-203.5 203.5-84.5zm704-416q106 0 181 75t75 181v896q0 106-75 181t-181 75h-1408q-106 0-181-75t-75-181v-896q0-106 75-181t181-75h224l51-136q19-49 69.5-84.5t103.5-35.5h512q53 0 103.5 35.5t69.5 84.5l51 136h224zm-704 1152q185 0 316.5-131.5t131.5-316.5-131.5-316.5-316.5-131.5-316.5 131.5-131.5 316.5 131.5 316.5 316.5 131.5z"/>
      </svg>
      <video className="dbrScanner-video" playsInline={true}/>
      <canvas className="dbrScanner-cvs-drawarea"/>
      <div className="dbrScanner-cvs-scanarea">
        <div className="dbrScanner-scanlight"/>
      </div>
    </div>
  );
};

export default BarcodeScanner;
